<style lang="scss" scoped>
@import "$vars";
div {
  strong {
    display: block;
    margin-bottom: 10px !important
  }
  span {
    display: flex;
  }
  display: block;
  margin-bottom: 20px
}
.not-anamnesis {
  margin-top: 3rem
}

h3 {
  color: #4a4a4a;
  font-family: $base-font-family;
  font-size: 18px;
  font-weight: 600;
}

h2 {
  color: #4a4a4a !important;
}

// Custom anamnesis style
.anamnesis {
  box-sizing: border-box;

  &__header {
    align-items: flex-start;
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin: 0;
  }

  &__description {
    align-items: flex-start;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    margin: 0;
    padding: 0;
  }

  &__title {
    box-sizing: border-box;
    display: block;
    margin: 0 0 5px 0;
  }

  &__separator {
    margin: 10px 0 20px 0;
  }

  &__wrapper {
    box-sizing: border-box;
    padding: 0;
    margin: 0;
  }

  &__question {
    align-items: flex-start;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    justify-content: flex-start;
    margin-bottom: 10px;
  }
}

.question {
  &__title {
    box-sizing: border-box;
    color: #616161;
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0;
    text-transform: unset;
  }

  &__field {
    align-items: center;
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-start;
    width: 100%;

    &--text,
    &--textarea,
    &--select,
    &--number,
    &--date {
      .form-input,
      .form-select,
      .form-date .form-input,
      .form-date .form-input:read-only {
        background-color: #fff;
        border: 1px solid #DEE1E2;
        box-sizing: border-box;
        margin: 5px 1px;
        width: 100%;
      }

      position: relative;
    }

    &--select,
    &--checkbox,
    &--radio {
      label {
        font-size: 0.7rem;
      }
    }

    &--checkbox {
      .form-checkbox {
        width: calc(100% / 3);
      }
    }

    &--radio {
      .form-radio:not(:first-of-type) {
        margin-left: 20px;
      }
    }
  }
}

div {
  &.loader.flex-center {
    padding: 40px;
  }
}

.error {
  box-shadow: 0 0 0 0.1rem rgba(248, 83, 89, 0.2) !important;
  border-bottom: 1px solid rgb(248, 83, 89) !important;
  border-color: rgb(248, 83, 89) !important;
}

.error-blur {
  box-shadow: 0 0 0 0.1rem rgba(248, 83, 89, 0.2) !important;
}
</style>

<template>
  <main class="component-patients">
    <dr-loader
      v-if="!loaded"
      size="lg"
    />

    <transition
      name="fade"
      mode="in-out"
    >
      <div
        v-if="!loaded"
        class="spinner"
      >
        <div class="spinner__spinner" />
      </div>
    </transition>

    <template v-if="!customAnamnesisId && loaded">
      <h2>Anamnese</h2>

      <hr />

      <form @submit.prevent="submit()">
        <template v-for="(field, name) in formData">
          <dr-field
            v-if="!field.hide"
            :ref="name"
            :key="'main_'+name"
            :id="name"
            :form.sync="formData"
            :validators.sync="$v.formData[name]"
            :field="field"
            :apiErrors.sync="apiErrors[name]"
            class="anamnesisField"
          />
        </template>

        <dr-submit
          :disabled="$v.$invalid"
          :sending="sending"
          class="btn-sm"
          text="Salvar"
        />
      </form>
    </template>

    <template v-if="customAnamnesisId && loaded">
      <article class="anamnesis">
        <header class="anamnesis__header">
          <div class="anamnesis__description">
            <h2 class="anamnesis__title">{{ anamnesis.name }}</h2>
          </div>
        </header>

        <hr class="anamnesis__separator" v-if="loaded">

        <div class="anamnesis__wrapper" v-if="loaded">
          <div
            class="anamnesis__question question"
            v-for="(question, i) in anamnesis.fields"
            :key="`${customAnamnesisId}_anamnesis_question_${i}`"
          >
            <label class="question__title">
              {{ question.name }}
            </label>

            <template v-if="question['field_type'] === 'TEXT'">
              <div class="question__field question__field--text">
                <input
                  v-model="question.value"
                  :key="`${customAnamnesisId}_anamnesis_question_${i}_text`"
                  type="text"
                  :class="{ 'error': question.isDirty }"
                  class="form-input"
                  @blur="question.value ? question.isDirty = false : question.isDirty = true"
                >
              </div>
            </template>

            <template v-if="question['field_type'] === 'TEXTAREA'">
              <div class="question__field question__field--textarea">
                <textarea
                  v-model="question.value"
                  :key="`${customAnamnesisId}_anamnesis_question_${i}_textarea`"
                  :ref="`${customAnamnesisId}_anamnesis_question_${i}_textarea`"
                  class="form-input"
                  :class="{ 'error': question.isDirty }"
                  @input="autoResizeTextarea($event)"
                  @blur="question.value ? question.isDirty = false : question.isDirty = true"
                >
                </textarea>
              </div>
            </template>

            <template v-if="question['field_type'] === 'NUMBER'">
              <div class="question__field question__field--number">
                <input
                  type="number"
                  :class="{ 'error': question.isDirty }"
                  class="form-input"
                  :key="`${customAnamnesisId}_anamnesis_question_${i}_number`"
                  v-model.number="question.value"
                  @blur="question.value ? question.isDirty = false : question.isDirty = true"
                >
              </div>
            </template>

            <template v-if="question['field_type'] === 'DATE'">
              <div
                v-click-outside="() => { question.showCalendar = false }"
                @click.self="question.showCalendar = !question.showCalendar"
                class="filters__field-datepicker question__field question__field--date"
              >
                <input
                  :key="`${customAnamnesisId}_anamnesis_question_${i}_date`"
                  :value="$moment(question.value).format('DD/MM/YYYY')"
                  :class="{ 'error': question.isDirty }"
                  class="form-input"
                  type="text"
                  placeholder="Escolha a data"
                  @focus="question.showCalendar = true"
                  @blur="question.value ? question.isDirty = false : question.isDirty = true"
                >
                <div class="agenda-settings__date-picker--wrapper" style="top: 100%;">
                  <dr-calendar
                    v-show="question.showCalendar"
                    v-model="question.value"
                    @change="question.showCalendar = false"
                  />
                </div>
              </div>
            </template>

            <template v-if="question['field_type'] === 'SELECT'">
              <div class="question__field question__field--select">
                <select
                  :class="{ 'error': question.isDirty }"
                  class="form-select"
                  :key="`${customAnamnesisId}_anamnesis_question_${i}_select`"
                  v-model="question.value"
                  @blur="question.value ? question.isDirty = false : question.isDirty = true"
                >
                  <option
                    v-for="(option, j) in question['custom_anamnesis_question_options']"
                    :key="`${customAnamnesisId}_anamnesis_question_${i}_select_option_${j}`"
                    :value="option.id"
                  >
                    {{ option.name }}
                  </option>
                </select>
              </div>
            </template>

            <template v-if="question['field_type'] === 'CHECKBOX'">
              <div
                :class="{ 'error-blur': question.isDirty }"
                class="question__field question__field--checkbox"
              >
                <label
                  class="form-checkbox"
                  v-for="(option, j) in question['custom_anamnesis_question_options']"
                  :key="`${customAnamnesisId}_anamnesis_question_${i}_label_${j}`"
                >
                  <input
                    type="checkbox"
                    :name="`anamnesis_question_${i}_checkbox_option`"
                    :key="`${customAnamnesisId}_anamnesis_question_${i}_checkbox_option_${j}`"
                    :value="option.id"
                    v-model="question.value"
                    @blur="question.value ? question.isDirty = false : question.isDirty = true"
                  >
                  <i class="form-icon" />
                  {{ option.name }}
                </label>
              </div>
            </template>

            <template v-if="question['field_type'] === 'RADIO'">
              <div
                :class="{ 'error-blur': question.isDirty }"
                class="question__field question__field--radio"
              >
                <label
                  class="form-radio"
                  v-for="(option, j) in question['custom_anamnesis_question_options']"
                  :key="`${customAnamnesisId}_anamnesis_question_${i}_label_${j}`"
                >
                  <input
                    type="radio"
                    :key="`${customAnamnesisId}_anamnesis_question_${i}_radio_option_${j}`"
                    :name="`anamnesis_question_${i}_radio_option`"
                    :value="option.id"
                    v-model="question.value"
                    @blur="question.value ? question.isDirty = false : question.isDirty = true"
                  >
                  <i class="form-icon" />
                  {{ option.name }}
                </label>
              </div>
            </template>
          </div>
        </div>

        <footer class="anamnesis__footer">
          <dr-submit
            :disabled="!anamnesis.valid"
            :sending="anamnesis.sending"
            class="btn-sm"
            text="Salvar"
            @click="submit()"
          />
        </footer>

        <div class="loader flex-center" v-if="!anamnesis.loaded">
          <dr-loader
            v-if="!loaded"
            :key="'dr-loader'"
            size="lg"
            class="loader flex-center"
          />
        </div>
      </article>
    </template>
  </main>
</template>

<script>
import drField from '@/components/form/field'
import drSubmit from '@/components/form/submit'
import { formData, validations } from '@/data/formData/anamnesis'
import { DrCalendar } from 'cm2tech-vue-calendar'
import EventBus from '@/globals/eventBus'
import { AUTO_SAVE_INTERVAL } from '@/globals/constants'

export default {
  props: {
    patient: {
      type: Object,
      required: true
    },

    appointment: {
      type: Object,
      required: true
    },

    customAnamnesis: {
      type: Number,
      required: false
    }
  },

  components: {
    drField,
    drSubmit,
    DrCalendar
  },

  data () {
    return {
      loaded: false,
      sending: false,
      apiErrors: this.fn_BUILD_API_ERRORS(formData),
      anamnesisId: null,
      anamnesis: {
        sending: false,
        loaded: false,
        valid: false,
        id: null,
        name: null,
        fields: [],
        firstState: []
      },
      formData,
      firstStateFormData: null,
      autoSave: null
    }
  },

  validations: {
    formData: validations
  },

  computed: {
    customAnamnesisId () {
      return this.customAnamnesis
    },

    isCustomAnamnesisFormValid () {
      if (this.customAnamnesis) {
        return this.anamnesis.fields.some((field) => {
          if (Array.isArray(field.value) && field.value.length === 0) {
            return true
          }

          if (field.value === '' || !field.value) {
            return true
          }

          return false
        })
      }

      return false
    }
  },

  watch: {
    customAnamnesisId: {
      immediate: false,
      deep: false,
      handler (next, prev) {
        if (next !== prev) {
          this.init()
        }
      }
    },

    'anamnesis.fields': {
      immediate: true,
      deep: true,
      handler (next, prev) {
        this.validateAnamnesis()
      }
    }
  },

  created () {
    EventBus.$on('submit-anamnesis', () => {
      if (this.hasUnsavedData()) {
        this.submit()
      }
    })

    this.anamnesis.firstState = null
    this.firstStateFormData = null
  },

  mounted () {
    this.init()
  },

  destroyed () {
    this.destroyAutoSave()
  },

  methods: {
    init () {
      this.destroyAutoSave()

      if (!this.customAnamnesis) {
        const url = `/doctor/agenda?filterAnd[a.id]=${this.appointment.id}`
        this.loaded = false
        this.fn_SHOW_PROGRESS()
        this.$http
          .get(url, this.$store.getters.getAuthHeader)
          .then((res) => (this.anamnesisId = res.data.data[0].anamnesisId))
          .then(() => {
            if (this.anamnesisId) {
              return this.$http
                .get(`/anamnesis/${this.anamnesisId}`, this.$store.getters.getAuthHeader)
            }

            return null
          })
          .then((res) => {
            if (res) {
              for (let f in res.data) {
                let field = this.fn_TO_CAMELCASE(f)
                if (this.formData[field]) {
                  if (typeof res.data[f] === 'boolean') {
                    this.formData[field].value = res.data[f] ? 1 : 0
                  } else {
                    this.formData[field].value = res.data[f]
                  }
                }
              }
            }
          })
          .catch((err) => console.log(err))
          .finally(() => {
            let firstState = []
            Object.keys(this.formData).forEach((key) => {
              firstState.push({ key, value: this.formData[key].value })
            })
            this.firstStateFormData = JSON.parse(JSON.stringify(firstState))

            this.loaded = true
            this.fn_HIDE_PROGRESS()
            this.createAutoSave()
          })
      }

      if (this.customAnamnesis) {
        this.fn_SHOW_PROGRESS()
        this.loaded = false
        this.resetAnamnesis()
        this.getAnamnesis(this.customAnamnesisId)
          .then((res) => {
            const fields = res['custom_anamnesis_questions'].map((answer) => {
              return { ...answer, value: null, showCalendar: false, isDirty: false }
            })

            this.anamnesis.fields = fields.sort((a, b) => a.position > b.position)
            this.anamnesis.id = res.id
            this.anamnesis.name = res.name
          })
          .then(() => this.fillCustomAnamnesis())
          .catch(err => console.warn('err\n', err))
          .finally(() => {
            const fields = this.anamnesis.fields.map((field) => {
              return {
                field: field.name,
                value: field.value
              }
            })

            this.anamnesis.firstState = JSON.parse(JSON.stringify(fields))

            this.loaded = true
            this.fn_HIDE_PROGRESS()
            this.createAutoSave()
          })
      }
    },

    autoResizeTextarea (event) {
      const field = event.target
      const computed = window.getComputedStyle(field)

      field.style.height = 'inherit'

      const height = parseInt(computed.getPropertyValue('border-top-width'), 10) +
        parseInt(computed.getPropertyValue('padding-top'), 10) +
        field.scrollHeight +
        parseInt(computed.getPropertyValue('padding-bottom'), 10) +
        parseInt(computed.getPropertyValue('border-bottom-width'), 10)

      field.style.height = `calc(${height}px)`
    },

    submit ({ showConfirmation = true, nextSection = null, nextTab = null } = {}) {
      this.destroyAutoSave()

      if (!this.customAnamnesis && this.$v.$invalid) {
        if (showConfirmation) {
          this.$store.dispatch('setModalAlert', {
            title: 'Atenção',
            body: 'Este formulário contém dados inválidos e não foi possível salvá-lo.',
            size: 'md',
            active: true
          })

          this.$v.touch()
        }

        this.createAutoSave()
        return
      }

      if (!this.customAnamnesis && !this.$v.$invalid) {
        this.submitDefaultAnamnesis({ showConfirmation, nextSection, nextTab })
        return
      }

      if (this.customAnamnesis && !this.anamnesis.valid) {
        if (showConfirmation) {
          this.$store.dispatch('setModalAlert', {
            title: 'Atenção',
            body: 'Este formulário contém dados inválidos e não foi possível salvá-lo.',
            size: 'md',
            active: true
          })

          this.anamnesis.fields.forEach((field) => {
            if (Array.isArray(field.value) && field.value.length === 0) {
              this.$set(field, 'isDirty', true)
              return
            }

            if (field.value === '' || !field.value) {
              this.$set(field, 'isDirty', true)
            }
          })
        }

        this.createAutoSave()
        return
      }

      if (this.customAnamnesis && this.anamnesis.valid) {
        this.submitCustomAnamnesis({ showConfirmation, nextSection, nextTab })
      }
    },

    submitDefaultAnamnesis ({ showConfirmation = true, nextSection = null, nextTab = null }) {
      this.destroyAutoSave()
      EventBus.$emit('appointment-start-saving')

      this.sending = true
      this.fn_SHOW_PROGRESS()
      let data = this.fn_EXPAND_OBJECT(this.formData)
      data.agenda = this.appointment.id
      let url = `/anamnesis`
      let method = 'post'

      if (this.anamnesisId) {
        url = url + `/${this.anamnesisId}`
        method = 'put'
      }
      this.$http({
        url,
        method,
        data: { anamnesis: data },
        headers: this.$store.getters.getAuthHeader.headers
      }).then(
        (res) => {
          this.anamnesisId = res.data
          if (showConfirmation) {
            this.$store.dispatch('setModalAlert', {
              title: 'Sucesso',
              body: 'Anamnese enviada com sucesso',
              size: 'md',
              active: true
            })
            setTimeout(() => {
              this.$store.dispatch('setModalAlert', {
                active: false
              })
            }, 1000)
          }
          this.sending = false

          let firstState = []
          Object.keys(this.formData).forEach((key) => {
            firstState.push({ key, value: this.formData[key].value })
          })
          this.firstStateFormData = JSON.parse(JSON.stringify(firstState))
        }
      ).catch(
        (err) => {
          console.log(err)
        }
      ).finally(() => {
        this.sending = false
        this.fn_HIDE_PROGRESS()

        if (nextSection) EventBus.$emit('consultation-change-section', nextSection)
        if (nextTab) EventBus.$emit('consultation-change-tab', nextTab)

        EventBus.$emit('appointment-stop-saving')
        this.createAutoSave()
      })
    },

    async getAnamnesis (id) {
      const { data } = await this.$http.get(`/doctor/custom_anamnesis/${id}`, this.$store.getters.getAuthHeader)
      return data
    },

    submitCustomAnamnesis ({ showConfirmation = true, nextSection = null, nextTab = null }) {
      this.validateAnamnesis()

      if (this.anamnesis.valid) {
        this.destroyAutoSave()
        const agendaId = this.$route.params.agendaId
        const anamnesisId = this.anamnesis.id
        const headers = this.$store.getters.getAuthHeader.headers
        const url = `/agenda/${agendaId}/custom_anamnesis/${anamnesisId}/custom_anamnesis_question_answer`

        const validacao = this.anamnesis.fields.every((field) => {
          return Array.isArray(field.value)
            ? field.value.length > 0
            : field.value !== null
        })

        const method = validacao ? 'put' : 'post'

        const answers = {}
        this.anamnesis.fields.forEach((field) => {
          const { id, value } = field
          const newValue = (value instanceof Date)
            ? String(this.$moment(value).format('YYYY-MM-DD'))
            : value
          answers[id] = newValue
        })

        const data = { custom_anamnesis_question_answer: answers }

        this.anamnesis.sending = true
        this.$http({ method, url, data, headers })
          .then((res) => {
            if (showConfirmation) {
              const msg = validacao ? 'salva' : 'enviada'
              this.$store.dispatch('setModalAlert', {
                title: 'Sucesso',
                body: `Anamnese ${msg} com sucesso`,
                size: 'md',
                active: true
              })
              setTimeout(() => {
                this.$store.dispatch('setModalAlert', {
                  active: false
                })
              }, 2000)
            }

            const fields = this.anamnesis.fields.map((field) => {
              return {
                field: field.name,
                value: field.value
              }
            })

            this.anamnesis.firstState = JSON.parse(JSON.stringify(fields))

            if (nextSection) EventBus.$emit('consultation-change-section', nextSection)
            if (nextTab) EventBus.$emit('consultation-change-tab', nextTab)
          })
          .catch((err) => {
            console.warn('err\n', err)
            this.$store.dispatch('setModalAlert', {
              title: 'Erro',
              body: 'Ocorreu um erro',
              size: 'md',
              active: true
            })
          })
          .finally(() => {
            this.anamnesis.sending = false
            this.createAutoSave()
          })
      }
    },

    async getCustomAnamnesisAnswers (agendaId) {
      const { data } = await this.$http.get(
        `/agenda/${agendaId}/custom_anamnesis_question_answer?itensPerPage=500`,
        this.$store.getters.getAuthHeader
      )
      return data
    },

    async fillCustomAnamnesis () {
      const { data } = await this.getCustomAnamnesisAnswers(this.appointment.id)
      const anamnesisAnswers = data || null

      if (anamnesisAnswers) {
        this.anamnesis.fields.forEach((field) => {
          const answer = (field['field_type'] === 'CHECKBOX')
            ? anamnesisAnswers.filter(answer => answer['custom_anamnesis_question'].id === field.id)
            : anamnesisAnswers.find(answer => answer['custom_anamnesis_question'].id === field.id)

          field.value = []
          field['field_type'] === 'DATE' && (field.value = this.$moment().toISOString())

          if (answer && Array.isArray(answer)) {
            answer.forEach(a => field.value.push(a.name))
          }

          if (answer && !Array.isArray(answer) && answer !== null) {
            field.value = answer.name
          }
        })
      }
    },

    validateAnamnesis () {
      const isValid = this.anamnesis.fields.every((field) => {
        return (Array.isArray(field.value))
          ? field.value.length > 0
          : field.value !== null
      })

      this.anamnesis.valid = isValid
    },

    resetAnamnesis () {
      this.anamnesis.sending = false
      this.anamnesis.loaded = false
      this.anamnesis.id = null
      this.anamnesis.name = null
      this.anamnesis.fields = []
    },

    hasUnsavedData () {
      let firstState = null
      let newState = null

      if (this.customAnamnesis) {
        firstState = JSON.parse(JSON.stringify(this.anamnesis.firstState))

        const fields = this.anamnesis.fields.map((field) => {
          return {
            field: field.name,
            value: field.value
          }
        })

        newState = JSON.parse(JSON.stringify(fields))
      }

      if (!this.customAnamnesis) {
        firstState = JSON.parse(JSON.stringify(this.firstStateFormData))

        let prevState = []
        Object.keys(this.formData).forEach((key) => {
          prevState.push({ key, value: this.formData[key].value })
        })

        newState = JSON.parse(JSON.stringify(prevState))
      }

      return String(JSON.stringify(firstState)) !== String(JSON.stringify(newState))
    },

    autoSaveFn () {
      if (this.hasUnsavedData()) {
        this.submit({ showConfirmation: false })
      }
    },

    destroyAutoSave () {
      clearInterval(this.autoSave)
      this.autoSave = null
    },

    createAutoSave () {
      this.autoSave = setInterval(this.autoSaveFn, AUTO_SAVE_INTERVAL * 1000)
    }
  }
}
</script>
